import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, Phone, MapPin, MessageSquare, Send, Clock, Headphones } from 'lucide-react';
import { Input, TextArea, Button } from '../../components/shared';
import { contactService } from '../../services/contactService';
import { siteSettingsService } from '../../services/siteSettingsService';
import { useToastContext } from '../../contexts/ToastContext';

const ContactUs = () => {
  const toast = useToastContext();
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });
  const settings = settingsResponse?.data || {};
  const contactEmail = settings.site_email || 'hello@vendalist.com';
  const contactPhone = settings.site_phone || '1-800-VENDA-LIST';
  const contactAddress = settings.site_address || '123 Business Street';

  const tabs = [
    { id: 'general', label: 'General Inquiry', icon: MessageSquare },
    { id: 'booking', label: 'Booking Support', icon: Mail },
    { id: 'technical', label: 'Technical Issue', icon: Headphones },
    { id: 'vendor', label: 'Vendor Inquiry', icon: MessageSquare },
  ];

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: contactEmail,
      subtitle: 'We respond within 24 hours',
    },
    {
      icon: Phone,
      title: 'Phone',
      content: contactPhone,
      subtitle: 'Mon-Fri, 9am-6pm EST',
    },
    {
      icon: MapPin,
      title: 'Address',
      content: contactAddress,
      subtitle: 'Accra, Ghana',
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await contactService.submit({
        ...formData,
        inquiryType: activeTab,
      });
      toast.success(null, "Thank you for your message! We'll get back to you soon.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: activeTab,
      });
    } catch (err) {
      toast.error(null, err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 -mt-16">
      {/* Hero Section - extends behind transparent header */}
      <div className="bg-gradient-to-br from-black to-gray-900 text-white pt-16 pb-12 sm:pt-20 sm:pb-16">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
              <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 animate-bounce-gentle" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              Contact Us
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300">
              We're here to help! Get in touch with our team
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-container py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 md:p-6 text-center"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-full mb-2 sm:mb-3 md:mb-4">
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                    {info.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-gray-700 mb-1 break-words">{info.content}</p>
                  <p className="text-xs text-gray-500">{info.subtitle}</p>
                </div>
              );
            })}
          </div>

          {/* Contact Form with Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Tabs Navigation */}
            <div className="border-b border-gray-200">
              <nav className="flex overflow-x-auto -mb-px px-2 sm:px-4 md:px-6 scrollbar-hide">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setFormData(prev => ({ ...prev, inquiryType: tab.id }));
                      }}
                      className={`relative py-3 sm:py-4 px-2 sm:px-3 md:px-6 font-medium text-xs sm:text-sm md:text-base transition-all duration-300 whitespace-nowrap border-b-2 flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ${
                        activeTab === tab.id
                          ? "text-black border-black"
                          : "text-gray-600 border-transparent hover:text-black hover:border-gray-300"
                      }`}
                    >
                      <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0" />
                      <span className="hidden xs:inline sm:hidden md:inline">{tab.label}</span>
                      <span className="xs:hidden sm:inline md:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6 lg:p-8">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="text-sm sm:text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="text-sm sm:text-base"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Brief description"
                      className="text-sm sm:text-base"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <TextArea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    rows="5"
                    className="text-sm sm:text-base"
                    required
                  />
                </div>

                <div className="flex items-center justify-end pt-2 sm:pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    className="w-full sm:w-auto text-sm sm:text-base !bg-gray-900 hover:!bg-black !border-gray-900 focus:ring-gray-500"
                  >
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    Send Message
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Response Time Info */}
          <div className="mt-4 sm:mt-6 md:mt-8 bg-gray-100 border border-gray-200 rounded-xl p-3 sm:p-4 md:p-6">
            <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1">
                  Response Time
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-700">
                  We typically respond to inquiries within 24 hours during business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;

